import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tv, Mail, Phone, Globe, Building2 } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "new", label: "Nuova", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  { value: "contacted", label: "Contattata", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  { value: "negotiation", label: "Negoziazione", cls: "bg-purple-500/15 text-purple-400 border-purple-500/30" },
  { value: "won", label: "Chiusa (won)", cls: "bg-green-500/15 text-green-400 border-green-500/30" },
  { value: "lost", label: "Persa", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
];
const statusMeta = (v) => STATUS_OPTIONS.find((s) => s.value === v) || STATUS_OPTIONS[0];

const PACKAGE_LABELS = {
  starter: "Starter · Product Spotlight",
  growth: "Growth · Story + Commerce",
  premium: "Premium · Brand Documentary",
  season: "Season Partner",
  launch: "Launch Prodotto",
  custom: "Progetto Custom",
};

export default function AdminMediaRequests() {
  const [data, setData] = useState({ requests: [], total: 0, by_status: {} });
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    api.get("/admin/media/requests")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Errore caricamento"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.post(`/admin/media/requests/${id}/status`, { status });
      toast.success("Stato aggiornato");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const totalRevenue = data.requests
    .filter((r) => r.status === "won")
    .reduce((s, r) => s + (r.package_price || 0), 0);

  return (
    <div data-testid="admin-media-page" className="container-xl py-8">
      <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] mb-2">Super Admin · Amazonico TV</div>
      <h1 className="editorial-heading text-4xl flex items-center gap-3"><Tv className="h-8 w-8 text-[hsl(var(--primary))]" />Richieste Media Kit</h1>
      <p className="text-muted-foreground mt-2 mb-6">Pacchetti promozionali YouTube + Amazonico TV richiesti da aziende e brand.</p>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="bg-card border border-border p-4 rounded-sm">
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Totale</div>
          <div className="text-2xl font-bold mt-1">{data.total}</div>
        </div>
        {STATUS_OPTIONS.map((s) => (
          <div key={s.value} className={`border p-4 rounded-sm ${s.cls}`}>
            <div className="text-xs uppercase tracking-wider opacity-80">{s.label}</div>
            <div className="text-2xl font-bold mt-1">{data.by_status[s.value] || 0}</div>
          </div>
        ))}
      </div>
      {totalRevenue > 0 && (
        <div className="bg-[hsl(var(--primary))] text-white p-4 rounded-sm mb-6 flex justify-between items-center">
          <div>
            <div className="text-xs uppercase tracking-wider opacity-80">Revenue confermata (won)</div>
            <div className="text-3xl font-bold">€{totalRevenue.toLocaleString("it-IT")}</div>
          </div>
          <Tv className="h-10 w-10 opacity-40" />
        </div>
      )}

      {loading ? (
        <div className="text-center text-muted-foreground py-12">Caricamento...</div>
      ) : data.requests.length === 0 ? (
        <div className="bg-card border border-border p-12 text-center rounded-sm">
          <Tv className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
          <div className="text-lg font-semibold">Nessuna richiesta ancora</div>
          <div className="text-sm text-muted-foreground mt-1">Le richieste dalle aziende appariranno qui.</div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Azienda</TableHead>
                <TableHead>Contatto</TableHead>
                <TableHead>Pacchetto</TableHead>
                <TableHead>Valore</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Stato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.requests.map((r) => {
                const meta = statusMeta(r.status);
                return (
                  <TableRow key={r.id} data-testid={`media-req-${r.id}`}>
                    <TableCell className="max-w-[240px]">
                      <div className="font-semibold truncate flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-muted-foreground" />{r.company_name}</div>
                      {r.vat_number && <div className="text-[10px] font-mono text-muted-foreground">P.IVA {r.vat_number}</div>}
                      {r.website && <a href={r.website} target="_blank" rel="noreferrer" className="text-[10px] text-[hsl(var(--primary))] flex items-center gap-1 truncate"><Globe className="h-3 w-3" />{r.website}</a>}
                      {r.message && <div className="text-xs text-muted-foreground mt-1 line-clamp-2" title={r.message}>{r.message}</div>}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">{r.contact_name}</div>
                      <a href={`mailto:${r.email}`} className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1"><Mail className="h-3 w-3" />{r.email}</a>
                      {r.phone && <a href={`tel:${r.phone}`} className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1"><Phone className="h-3 w-3" />{r.phone}</a>}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-semibold">{PACKAGE_LABELS[r.package_id] || r.package_name}</div>
                      {r.product_category && <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{r.product_category}</div>}
                    </TableCell>
                    <TableCell className="font-bold text-[hsl(var(--primary))] whitespace-nowrap">€{Number(r.package_price || 0).toLocaleString("it-IT")}</TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(r.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</TableCell>
                    <TableCell>
                      <Select value={r.status || "new"} onValueChange={(v) => updateStatus(r.id, v)}>
                        <SelectTrigger data-testid={`media-req-status-${r.id}`} className={`h-8 text-xs border ${meta.cls}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
